<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { registerApi } from '@/api'
import type { ClassRegister, RegisterLesson, StudentWithAttendance, AttendanceState } from '@/api'
import { ClipboardCheck } from 'lucide-vue-next'
import { EmptyState, mix, type PaletteHue } from '@/design-system/components'
import Monogram from '@/components/classes/Monogram.vue'

/**
 * AttendanceTab — class register as a students × lessons grid (mirrors the
 * Android attendance table). Sticky student column, a per-student % present
 * summary, and lesson columns (newest first) whose headers link to the lesson
 * detail page where attendance is actually recorded.
 */
const props = defineProps<{ classId: string; hue: PaletteHue }>()
const router = useRouter()

const SW = 200
const AW = 72
const CW = 92
const headBand = computed(() => mix(props.hue.solid, 0.1))
const colBorder = computed(() => mix(props.hue.solid, 0.18))
const zebra = computed(() => mix(props.hue.solid, 0.025))

const loading = ref(true)
const students = ref<StudentWithAttendance[]>([])
const lessons = ref<RegisterLesson[]>([])

// Newest first.
// Column order mirrors Android: the next lesson to take (earliest of today/future)
// leads, then past lessons newest-first, then the remaining future lessons trail —
// so empty future columns sit at the far right instead of burying recorded ones.
const orderedLessons = computed(() => {
  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  const cutoff = startOfToday.getTime()
  const at = (l: RegisterLesson) => new Date(l.startTime).getTime()
  const upcoming = lessons.value.filter((l) => at(l) >= cutoff).sort((a, b) => at(a) - at(b))
  const past = lessons.value.filter((l) => at(l) < cutoff).sort((a, b) => at(b) - at(a))
  return [...upcoming.slice(0, 1), ...past, ...upcoming.slice(1)]
})
const minWidth = computed(() => SW + AW + orderedLessons.value.length * CW)

// (studentId|lessonId) → state
const stateMap = computed(() => {
  const m = new Map<string, AttendanceState>()
  for (const s of students.value) {
    for (const rec of s.attendance ?? []) {
      if (rec.state) m.set(`${s.student.id}|${rec.lessonId}`, rec.state)
    }
  }
  return m
})

const summary = computed(() => {
  const pcts = students.value.map((s) => s.summary?.averagePresentPercent ?? 0)
  const classPct = pcts.length ? Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length) : null
  let late = 0
  let total = 0
  for (const s of students.value) {
    const b = s.summary?.breakdown
    late += b?.LATE ?? 0
    total += (b?.PRESENT ?? 0) + (b?.LATE ?? 0) + (b?.ABSENT ?? 0)
  }
  const latePct = total ? Math.round((late / total) * 100) : null
  return { classPct, latePct }
})

function cellFor(studentId: string, lessonId: string): { letter: string; cls: string } {
  const st = stateMap.value.get(`${studentId}|${lessonId}`)
  if (st === 'PRESENT') return { letter: 'P', cls: 'att__mark--present' }
  if (st === 'LATE') return { letter: 'L', cls: 'att__mark--late' }
  if (st === 'ABSENT') return { letter: 'A', cls: 'att__mark--absent' }
  return { letter: '', cls: 'att__mark--none' }
}

function lessonDate(s: string): string {
  return new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}
function lessonTime(s: string): string {
  const d = new Date(s)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

async function fetchData() {
  loading.value = true
  try {
    const reg: ClassRegister = await registerApi.getClassRegister(props.classId)
    students.value = reg.students ?? []
    lessons.value = reg.lessons ?? []
  } catch {
    students.value = []
    lessons.value = []
  } finally {
    loading.value = false
  }
}
onMounted(fetchData)
</script>

<template>
  <div>
    <EmptyState
      v-if="!loading && !orderedLessons.length"
      :icon="ClipboardCheck"
      :hue="hue"
      title="No lessons to register"
      body="Once this class has lessons on the timetable, take the register here and track attendance over time."
    />

    <template v-else>
    <div class="att__summary">
      <span class="att__eyebrow">Attendance</span>
      <div class="att__stats">
        <span class="att__stat">
          <span class="att__stat-n">{{ summary.classPct == null ? '—' : summary.classPct }}<i v-if="summary.classPct != null">%</i></span>
          <span class="att__stat-l">present</span>
        </span>
        <span v-if="summary.latePct" class="att__late">{{ summary.latePct }}% late</span>
      </div>
    </div>

    <div class="att__card">
      <div class="att__scroll">
        <div class="att__inner" :style="{ minWidth: minWidth + 'px' }">
          <!-- header -->
          <div class="att__hrow" :style="{ background: headBand }">
            <div class="att__hstudent" :style="{ width: SW + 'px', background: headBand }">Student</div>
            <div class="att__havg" :style="{ width: AW + 'px', borderLeft: `1px solid ${colBorder}` }">%</div>
            <button
              v-for="l in orderedLessons"
              :key="l.id"
              type="button"
              class="att__hlesson"
              :style="{ width: CW + 'px', borderLeft: `1px solid ${colBorder}` }"
              @click="router.push(`/classes/${classId}/lesson/${l.id}`)"
            >
              <span class="att__hdate">{{ lessonDate(l.startTime) }}</span>
              <span class="att__htime">{{ lessonTime(l.startTime) }}</span>
            </button>
          </div>

          <!-- rows -->
          <div
            v-for="(s, ri) in students"
            :key="s.student.id"
            class="att__row"
            :style="{ background: ri % 2 ? zebra : 'var(--surface)' }"
          >
            <div class="att__student" :style="{ width: SW + 'px' }">
              <Monogram :first="s.student.firstName" :last="s.student.lastName" :key-id="s.student.id" :photo-url="s.student.photoUrl" :size="30" />
              <span class="att__sname"><b>{{ s.student.lastName }}</b>, {{ s.student.firstName }}</span>
            </div>
            <div class="att__avg" :style="{ width: AW + 'px' }">
              {{ s.summary?.averagePresentPercent != null ? Math.round(s.summary.averagePresentPercent) + '%' : '—' }}
            </div>
            <div
              v-for="l in orderedLessons"
              :key="l.id"
              class="att__cell"
              :style="{ width: CW + 'px' }"
            >
              <span class="att__mark" :class="cellFor(s.student.id, l.id).cls">
                {{ cellFor(s.student.id, l.id).letter }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="orderedLessons.length" class="att__foot">
      Tap a lesson heading to record or edit that lesson's attendance.
    </div>
    </template>
  </div>
</template>

<style scoped>
.att__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.att__eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--ink-400);
}
.att__stats {
  display: flex;
  align-items: center;
  gap: 12px;
}
.att__stat {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
}
.att__stat-n {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}
.att__stat-n i {
  font-style: normal;
  opacity: 0.55;
  font-weight: 500;
}
.att__stat-l {
  font-size: 12.5px;
  color: var(--ink-500);
}
.att__late {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--ink-500);
}
.att__card {
  border-radius: var(--radius-lg);
  box-shadow: var(--hairline-border);
  overflow: hidden;
  background: var(--surface);
}
.att__scroll {
  overflow-x: auto;
}
.att__inner {
  font-family: var(--font-sans);
}
.att__hrow {
  display: flex;
  border-bottom: 1px solid var(--outline-variant);
}
.att__hstudent {
  flex-shrink: 0;
  padding: 12px 16px;
  position: sticky;
  left: 0;
  z-index: 2;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--ink-500);
}
.att__havg {
  flex-shrink: 0;
  padding: 12px 0;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: var(--ink-500);
}
.att__hlesson {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 10px 6px;
  border-top: 0;
  border-right: 0;
  border-bottom: 0;
  background: transparent;
  cursor: pointer;
  transition: background var(--motion-fast);
}
.att__hlesson:hover {
  background: rgba(255, 255, 255, 0.5);
}
.att__hdate {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
}
.att__htime {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 10.5px;
  color: var(--ink-500);
}
.att__row {
  display: flex;
  border-top: 1px solid var(--hairline);
}
.att__student {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 8px 16px;
  position: sticky;
  left: 0;
  z-index: 1;
  background: inherit;
}
.att__sname {
  font-size: 13.5px;
  color: var(--ink-600);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.att__sname b {
  font-weight: 600;
  color: var(--text-primary);
}
.att__avg {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-600);
}
.att__cell {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-left: 1px solid var(--hairline);
}
.att__mark {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
}
.att__mark--present {
  background: var(--success-soft);
  color: var(--success-ink);
}
.att__mark--late {
  background: var(--warning-soft);
  color: var(--warning-ink);
}
.att__mark--absent {
  background: var(--danger-soft);
  color: var(--danger-ink);
}
.att__mark--none {
  border: 1.5px solid var(--ink-200);
}
.att__foot {
  margin-top: 10px;
  text-align: center;
  font-size: 12.5px;
  color: var(--ink-500);
}
</style>
